import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { RootStore } from 'stores/rootStore';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import useMediaQuery from '@material-ui/core/useMediaQuery';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import { CircularProgress } from '@material-ui/core';
import { EdwardColors } from 'enums/index';

const pdfWidth = 794; // PDF tempate width in px

interface Props {
  rootStore: RootStore;
  open: boolean;
  handleClose: () => void;
  handleDownload?: () => void;
  openDocument?: (Document & { loading: boolean }) | null;
  renderTemplate: (afterLoad?: () => void) => React.ReactElement<any>;
  miscFunction?: () => void;
  miscText?: string;
  miscIcon?: React.Component;
  miscMobileIcon?: React.Component;
  functionDisabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContent: {
      order: 1,
      margin: 0,
      maxWidth: '100%',
      '@media (max-width:1024px)': {
        padding: '0 !important',
      },
    },
    controls: {
      order: 0,
      background: '#000',
      padding: theme.spacing(1),
      '& .MuiButton-root.Mui-disabled': {
        color: '#ccc',
        borderColor: '#ccc',
      },
    },
    controlsWrapper: {
      maxWidth: `${pdfWidth}px`,
      margin: '0 auto',
      display: 'flex',
      width: '100%',
      justifyContent: 'space-between',
      position: 'relative',
      '& button': {
        maxHeight: '39px',
      },
    },
    action: {
      marginLeft: theme.spacing(0.5),
      '@media (max-width:400px)': {
        marginLeft: 0,
        '& .MuiButton-startIcon': {
          margin: 0,
          '& .MuiSvgIcon-root': {
            width: '1.5em',
            height: '1.5em',
          },
        },
      },
    },
    misc: {
      borderRadius: '24px',
      fontSize: '17px',
      '& .MuiButton-iconSizeMedium > *:first-child': {
        fontSize: '26px',
      },
      '@media (max-width:400px)': {
        '& .MuiButton-iconSizeMedium > *:first-child': {
          width: '2.1875rem',
          height: '2.1875rem',
        },
      },
    },
    close: {
      marginRight: theme.spacing(0.5),
      '@media (max-width:400px)': {
        marginRight: 0,
      },
    },
    zoom: {
      display: 'flex',
      maxWidth: 64 * 3,
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },
    zoomButton: {
      '& .MuiSvgIcon-root': {
        width: '2.1875rem',
        height: '2.1875rem',
      },
    },
    percentage: {
      color: EdwardColors.MEDIUM,
      minWidth: '70px',
      textAlign: 'center',
      fontSize: '1.5rem',
    },
  }),
);

const PdfPreview: React.FC<Props> = ({
  rootStore,
  open,
  handleClose,
  handleDownload,
  openDocument,
  renderTemplate,
  miscFunction,
  miscText,
  miscIcon,
  miscMobileIcon,
  functionDisabled
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [scaleSize, setScaleSize] = useState(0);
  const [outDisabled, setOutDisabled] = useState(false);
  const [inDisabled, setInDisabled] = useState(false);
  const [newMeasure, setNewMeasure] = useState(true);
  const mobile = useMediaQuery('(max-width:400px)', { noSsr: true });

  const closePreview = () => {
    handleClose();
    setScaleSize(-1);
    (document.getElementById('_PDF_DialogContent') as HTMLElement).setAttribute('style', 'visibility: hidden');
  };

  const basePage = useMemo(() => {
    const minWidth = Math.min(window.innerWidth, pdfWidth);
    return {
      minWidth: minWidth,
      width: pdfWidth,
      height: 0,
      scale: 1,
      sizeX: 0,
      sizeY: 0,
      step: 0,
      bigStep: 0,
      bigScreen: false,
    };
  }, []);

  const scalePage = useCallback(
    (diff?: number) => {
      if (!diff) {
        setScaleSize(basePage.minWidth);
      } else {
        const closeTo100 = diff >= 0.99 * basePage.width && diff <= 1.01 * basePage.width;
        const closeToMin = diff >= 0.99 * basePage.minWidth && diff <= basePage.minWidth * 1.01;
        setScaleSize(closeToMin ? basePage.minWidth : closeTo100 ? basePage.width : diff);
      }
    },
    [basePage.minWidth, basePage.width],
  );

  const wrapper = document.getElementById('_PDF_DialogContent') as HTMLElement;
  const controls = document.getElementById('_controls') as HTMLElement;
  const controlsWrapper = document.getElementById('_controls_wrapper') as HTMLElement;
  controlsWrapper &&
    controls.offsetWidth > wrapper.scrollWidth &&
    controlsWrapper.setAttribute('style', `left: -${(controls.offsetWidth - wrapper.scrollWidth) / 2}px`);

  const measurePage = useMemo(
    () => () => {
      rootStore.stores.uiStore.stopLoading('pdfPreview');
      const pdf = document.querySelector('._PDF_content') as HTMLElement | null;
      const wrapper = document.getElementById('_PDF_DialogContent') as HTMLElement;

      if (pdf) {
        basePage.width = pdf.offsetWidth;
        basePage.height = pdf.offsetHeight;
        basePage.minWidth = Math.min(wrapper.offsetWidth, basePage.width);
        basePage.bigScreen = basePage.width < wrapper.offsetWidth;
        const deltaW = !basePage.bigScreen
          ? basePage.width - wrapper.offsetWidth
          : wrapper.offsetWidth - basePage.width;
        basePage.bigStep = Math.round(pdf.offsetWidth / 4);
        basePage.step = basePage.bigScreen ? Math.floor(pdf.offsetWidth / 4) : Math.round(deltaW / 5);
        setNewMeasure(true);

        wrapper.onscroll = debounce(function handleScroll() {
          const sizeX = basePage.width * basePage.scale;
          const sizeY = basePage.height * basePage.scale;
          const scrollX = wrapper.scrollLeft;
          const scrollY = wrapper.scrollTop;

          if (scrollX + wrapper.offsetWidth > sizeX) {
            wrapper.scrollTo({ left: sizeX - wrapper.offsetWidth });
          }
          if (scrollY + wrapper.offsetHeight > sizeY) {
            wrapper.scrollTo({ top: sizeY - wrapper.offsetHeight });
          }
        }, 50);
      }
      scalePage();
    },
    [basePage, scalePage, rootStore.stores.uiStore],
  );

  useEffect(() => {
    if (scaleSize > 0) {
      setNewMeasure(false);
      const pdf = document.querySelector('._PDF_content') as HTMLElement | null;
      const wrapper = document.getElementById('_PDF_DialogContent') as HTMLElement;
      const percentage = document.getElementById('_percentage') as HTMLElement;
      if (pdf) {
        basePage.scale = scaleSize / (basePage.width || 1);
        const sizeX = basePage.width * basePage.scale;
        const fit = sizeX < wrapper.offsetWidth;
        let cssString = `transform-origin: 0 0; transform: scale(${basePage.scale})`;
        if (basePage.bigScreen && fit) {
          cssString += ` translate(-50%,0); position: relative; left: 50%`;
        }
        pdf.setAttribute('style', cssString);

        wrapper.setAttribute('style', 'visibility: visible');
        percentage.innerText = `${Math.floor(basePage.scale * 100)} %`;

        if (Math.floor(sizeX) >= basePage.width * 2) {
          setInDisabled(true);
        } else {
          setInDisabled(false);
        }

        if (
          (Math.floor(sizeX) <= wrapper.offsetWidth && basePage.scale < 1 && !basePage.bigScreen) ||
          (basePage.bigScreen && basePage.scale < 0.5)
        ) {
          wrapper.setAttribute('style', 'overflow-x: hidden');
          wrapper.scrollTo({ left: 0 });
          setOutDisabled(true);
        } else {
          wrapper.setAttribute('style', 'overflow-x: auto');
          setOutDisabled(false);
        }
      }
    }
  }, [newMeasure, scaleSize, basePage, rootStore.stores.uiStore]);

  useEffect(() => {
    const handleResizeDebounced = debounce(function handleResize() {
      measurePage();
    }, 50);

    window.addEventListener('resize', handleResizeDebounced);
    return () => window.removeEventListener('resize', handleResizeDebounced);
  }, [measurePage]);

  const template = useMemo(() => renderTemplate(measurePage), [renderTemplate, measurePage]);

  return (
    <Dialog open={open} style={{ margin: 0, height: '100%' }} fullScreen>
      <DialogContent id="_PDF_DialogContent" className={classes.dialogContent} style={{ visibility: 'hidden' }}>
        {template}
      </DialogContent>
      <DialogActions id="_controls" className={classes.controls}>
        <div id="_controls_wrapper" className={classes.controlsWrapper}>
          {handleDownload && (
            <Button
              variant={mobile ? 'text' : 'outlined'}
              color="primary"
              startIcon={openDocument?.loading ? <CircularProgress size={20} /> : <GetAppIcon />}
              onClick={() => handleDownload()}
              className={classes.action}
              disabled={functionDisabled}
            >
              {!mobile && t('general_download')}
            </Button>
          )}
          {miscFunction && (
            <Button
              variant={mobile ? 'text' : 'contained'}
              color="primary"
              startIcon={mobile && miscMobileIcon ? miscMobileIcon : miscIcon}
              onClick={() => miscFunction()}
              className={`${classes.action} ${classes.misc}`}
              disabled={functionDisabled}
            >
              {miscText && !mobile && t(miscText)}
            </Button>
          )}

          <div className={classes.zoom}>
            <Button
              color="primary"
              variant="text"
              onClick={() => {
                scalePage(scaleSize + (Math.floor(basePage.scale * 100) < 100 ? basePage.step : basePage.bigStep));
              }}
              disabled={inDisabled}
              className={classes.zoomButton}
            >
              <AddCircleOutlineIcon />
            </Button>
            <div id="_percentage" className={classes.percentage} />
            <Button
              color="primary"
              variant="text"
              onClick={() => {
                scalePage(scaleSize - (Math.floor(basePage.scale * 100) <= 100 ? basePage.step : basePage.bigStep));
              }}
              disabled={outDisabled}
              className={classes.zoomButton}
            >
              <RemoveCircleOutlineIcon />
            </Button>
          </div>
          <Button color="primary" variant="text" onClick={() => closePreview()} className={classes.close}>
            <CloseIcon fontSize="large" />
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default compose(inject('rootStore'), observer)(PdfPreview);
